import { call, take, put, fork, cancel, select } from 'redux-saga/effects'
import { eventChannel } from 'redux-saga'
import { createConsumer } from '@rails/actioncable'
import {
  userChannelConnected,
  userChannelDisconnected,
  userChannelConnectionError,
} from '../actions/cable'
import { fetchResourceSuccess, modules } from '../actions/resource'
import { reloadProjectResources } from './currentProject'
import { getStoredToken } from '../../lib/auth'
import { getCurrentProjectId, getModuleById } from '../reducers'
import { SIGN_IN_SUCCESS, SIGN_OUT_SUCCESS } from '../actions/authorization'

const CABLE_URL = process.env.CABLE_URL || 'http://localhost:3000/cable'
const PROJECT_CHANNEL_RECONNECTED = 'PROJECT_CHANNEL_RECONNECTED'

function buildCableURLWithJWT() {
  return `${CABLE_URL}?jwt_token=${getStoredToken()}`
}

function createUserChannelSubscriptions(consumer) {
  return eventChannel((emit) => {
    const subscription = consumer.subscriptions.create(
      { channel: 'UserChannel' },
      {
        connected: (_) => emit(userChannelConnected()),
        received: (data) => emit(data),
        disconnected: (_) => emit(userChannelDisconnected()),
      }
    )

    return (_) => subscription.disconnect()
  })
}

function* actionCableEventLoop(consumer, userChannelSubscription) {
  while (true) {
    try {
      const { type, payload, resource } = yield take(userChannelSubscription)

      if (type === userChannelConnected().type) {
        yield put(userChannelConnected())
      }

      if (type === userChannelDisconnected().type) {
        yield put(userChannelDisconnected())
      }

      if (resource?.operation === 'update') {
        yield put(fetchResourceSuccess(resource, payload, true))
      }
    } catch (error) {
      console.error(error)
    }
  }
}

function createProjectChannelSubscription(consumer, projectId) {
  return eventChannel((emit) => {
    let connectedBefore = false
    const subscription = consumer.subscriptions.create(
      { channel: 'ProjectChannel', project_id: projectId },
      {
        connected: () => {
          // The first confirmation follows the regular project load (project
          // switch saga, map). Later ones follow a dropped connection, during
          // which pushes may have been missed. A change in the short gap between
          // the initial load and the first confirmation is accepted.
          if (connectedBefore) emit({ type: PROJECT_CHANNEL_RECONNECTED })
          connectedBefore = true
        },
        received: (data) => emit(data),
      }
    )

    return (_) => subscription.unsubscribe()
  })
}

function* projectChannelEventLoop(projectChannelSubscription, projectId) {
  try {
    while (true) {
      const { type, payload, resource } = yield take(projectChannelSubscription)

      const currentProjectId = yield select(getCurrentProjectId)
      if (currentProjectId !== projectId) continue

      if (type === PROJECT_CHANNEL_RECONNECTED) {
        // Pushes sent while disconnected are lost: reload everything.
        yield* reloadProjectResources(projectId, { fresh: true })
      }

      if (resource?.operation === 'update') {
        if (resource.name === 'modules') {
          const module = yield select(getModuleById, payload.json.id)
          if (!module) {
            yield put(modules.read(payload.json.id, { projectId }))
            continue
          }
        }
        yield put(fetchResourceSuccess(resource, payload, true))
      }
    }
  } finally {
    // unsubscribe from the ProjectChannel when cancelled
    projectChannelSubscription.close()
  }
}

// Keeps a subscription to the ProjectChannel of the current project only.
// The current project is watched via selector (not only SET_CURRENT_PROJECT),
// because initially it is taken from the start project setting of the user.
export function* projectChannelFlow(consumer) {
  let projectId = null
  let eventLoopTask = null

  while (true) {
    const currentProjectId = yield select(getCurrentProjectId)

    if (currentProjectId !== projectId) {
      if (eventLoopTask) yield cancel(eventLoopTask)
      eventLoopTask = null
      projectId = currentProjectId

      if (projectId) {
        const projectChannelSubscription = yield call(
          createProjectChannelSubscription,
          consumer,
          projectId
        )
        eventLoopTask = yield fork(
          projectChannelEventLoop,
          projectChannelSubscription,
          projectId
        )
      }
    }

    // check again after the next action
    yield take('*')
  }
}

export default function* actionCableConnectionFlow(Client, Message) {
  yield call(function* () {
    while (true) {
      // wait for login
      yield take(SIGN_IN_SUCCESS)

      try {
        const consumer = createConsumer(buildCableURLWithJWT)

        const userChannelSubscription = yield call(
          createUserChannelSubscriptions,
          consumer
        )

        // start ActionCable event loop in background that consumes events from UserChannel
        const actionCableEventLoopTask = yield fork(
          actionCableEventLoop,
          consumer,
          userChannelSubscription
        )

        // subscribe to live updates of the current project in background
        const projectChannelTask = yield fork(projectChannelFlow, consumer)

        // wait for sign out
        yield take(SIGN_OUT_SUCCESS) // take USER_CHANNEL_CONNECTION_LOST here

        // cancel running tasks
        yield cancel(actionCableEventLoopTask)
        yield cancel(projectChannelTask)

        // disconnect ActionCable
        consumer.disconnect()
        // update state of action cable connection to be able to show disconnected state
      } catch (e) {
        console.error('Error connecting to UserChannel', e)
        yield put(userChannelConnectionError({ message: e.errorMessage }))
      }

      yield put(userChannelDisconnected())
    }
  })
}
